import React, { useState } from "react";
import { useChannels } from "../context/ChannelsContext";
import { toast } from "sonner";
import { MessageSquare, Send, X, ChevronDown, Smartphone, ExternalLink } from "lucide-react";

const META = {
  whatsapp: { label: "WhatsApp Web", Icon: Smartphone, color: "#15803D", bg: "#DCFCE7", reusable: false },
  telegram: { label: "Telegram Web", Icon: Send, color: "#1E40AF", bg: "#DBEAFE", reusable: true },
};

export default function ChannelsDock() {
  const { openChannel, closeChannel, openState, pinned } = useChannels();
  const [expanded, setExpanded] = useState(false);

  const handleOpen = (key) => {
    const ok = openChannel(key);
    if (ok === false) {
      toast.error("Popup bloccato dal browser. Abilita i popup per questo sito per usare i canali.");
    }
  };

  const hasPinned = pinned.whatsapp || pinned.telegram;
  const anyClosed = (pinned.whatsapp && !openState.whatsapp) || (pinned.telegram && !openState.telegram);

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3" data-testid="channels-dock">
      {expanded && (
        <div
          className="rounded-2xl shadow-2xl border w-72 overflow-hidden"
          style={{ background: "var(--surface)", borderColor: "var(--border)" }}
          data-testid="channels-dock-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <span className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>I miei canali</span>
            <button onClick={() => setExpanded(false)} className="p-1 rounded hover:bg-black/5" data-testid="channels-dock-collapse">
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          <div className="p-3 space-y-2">
            {Object.entries(META).map(([key, m]) => {
              const isOn = openState[key];
              return (
                <div
                  key={key}
                  className="flex items-center gap-3 p-2.5 rounded-xl border"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`channels-dock-item-${key}`}
                >
                  <div className="p-2 rounded-lg" style={{ background: m.bg }}>
                    <m.Icon className="w-4 h-4" style={{ color: m.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{m.label}</div>
                    <div className="text-[11px] flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                      {m.reusable ? (
                        <>
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full"
                            style={{ background: isOn ? "#16A34A" : "#9CA3AF" }}
                          />
                          {isOn ? "Aperto" : pinned[key] ? "Chiuso · riapri" : "Non collegato"}
                        </>
                      ) : (
                        <>Si apre in finestra esterna</>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleOpen(key)}
                    className="px-2.5 py-1.5 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-1 hover:brightness-110"
                    style={{ background: m.color }}
                    data-testid={`channels-dock-open-${key}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> {m.reusable ? ((isOn || pinned[key]) ? "Mostra" : "Apri") : "Apri"}
                  </button>
                  {m.reusable && pinned[key] && (
                    <button
                      onClick={() => closeChannel(key)}
                      className="p-1.5 rounded-lg hover:bg-black/5"
                      title="Scollega dal CRM"
                      data-testid={`channels-dock-close-${key}`}
                    >
                      <X className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
                    </button>
                  )}
                </div>
              );
            })}
            <p className="text-[10px] px-1 pt-1" style={{ color: "var(--text-muted)" }}>
              Le finestre restano agganciate al CRM: cambiando sezione si riaprono da qui con un click.
            </p>
          </div>
        </div>
      )}

      <button
        onClick={() => setExpanded((v) => !v)}
        className="relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white hover:brightness-110 transition-[transform,filter] active:scale-95"
        style={{ background: "var(--primary)" }}
        title="I miei canali"
        data-testid="channels-dock-toggle"
      >
        <MessageSquare className="w-6 h-6" />
        {hasPinned && (
          <span
            className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2"
            style={{
              background: anyClosed ? "#F59E0B" : "#16A34A",
              borderColor: "var(--surface)",
            }}
          />
        )}
      </button>
    </div>
  );
}
